import { ArrowRight, Building2, Mail, Pencil, Phone, Trash2 } from 'lucide-react';
import { formatMobileWithCountryCode } from '@/features/auth/utils/mobileNumber';
import type { ClientUser } from '../api/clientsApi';

function getName(c: ClientUser) {
  return (
    [c.firstName, c.lastName].filter(Boolean).join(' ') ||
    (typeof c.companyName === 'string' ? c.companyName : '') ||
    'Untitled client'
  );
}

function getInitials(c: ClientUser) {
  const parts = getName(c).split(/\s+/).filter(Boolean);
  return `${parts[0]?.[0] || 'C'}${parts[1]?.[0] || ''}`.toUpperCase();
}

function statusMeta(status?: string) {
  const s = String(status || '').toUpperCase();
  if (s === 'VERIFIED') {
    return {
      label: 'Verified',
      note: null as string | null,
      bg: 'bg-[#ECFDF5]',
      text: 'text-[#297857]',
      dot: 'bg-[#297857]',
    };
  }
  if (s === 'REJECTED') {
    return {
      label: 'Rejected',
      note: null as string | null,
      bg: 'bg-[#FEF2F2]',
      text: 'text-[#B91C1C]',
      dot: 'bg-[#EF4444]',
    };
  }
  if (!s) {
    return {
      label: 'Unknown',
      note: null as string | null,
      bg: 'bg-[var(--dash-soft)]',
      text: 'text-[var(--dash-muted)]',
      dot: 'bg-[#B0BCC9]',
    };
  }
  return {
    label: 'Not verified',
    note: 'Already active with another agency · Admin approval needed',
    bg: 'bg-[#FFFBEB]',
    text: 'text-[#B45309]',
    dot: 'bg-[#F59E0B]',
  };
}

type Props = {
  client: ClientUser;
  index?: number;
  onView: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
};

export function ClientListCard({
  client,
  index = 0,
  onView,
  onEdit,
  onDelete,
}: Props) {
  const name = getName(client);
  const company =
    typeof client.companyName === 'string' ? client.companyName : '';
  const phone = formatMobileWithCountryCode(
    client.mobileNumber,
    client.mobileCountryCode,
  );
  const status = statusMeta(
    String(client.status || client.isAdminVerified || ''),
  );
  const category =
    typeof client.businessCategory === 'string'
      ? client.businessCategory
      : '';
  const subCategory =
    typeof client.businessSubCategory === 'string'
      ? client.businessSubCategory
      : '';

  return (
    <article
      className="camp-fade-up group flex flex-col overflow-hidden rounded-2xl border border-[var(--dash-line)] bg-white shadow-[0_10px_28px_-22px_rgba(16,38,73,0.35)] transition hover:-translate-y-0.5 hover:border-[#C5D4E6] hover:shadow-[0_18px_40px_-24px_rgba(16,38,73,0.4)]"
      style={{ animationDelay: `${Math.min(index, 8) * 40}ms` }}
    >
      <div className="flex gap-3.5 p-4 pb-3">
        <div
          className="flex h-[56px] w-[56px] shrink-0 items-center justify-center rounded-xl text-sm font-bold text-white shadow-sm"
          style={{ background: 'var(--dash-gradient)' }}
        >
          {getInitials(client)}
        </div>
        <div className="min-w-0 flex-1 text-left">
          <div className="mb-1 flex items-start justify-between gap-2">
            <h3 className="m-0 line-clamp-1 text-[15px] font-semibold leading-snug text-[var(--dash-ink)]">
              {company || name}
            </h3>
            <span
              className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-1 text-[10px] font-bold tracking-wide uppercase ${status.bg} ${status.text}`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${status.dot}`} />
              {status.label}
            </span>
          </div>
          {status.note ? (
            <p className={`m-0 mb-1 text-[11px] font-medium ${status.text}`}>
              {status.note}
            </p>
          ) : null}
          <p className="m-0 truncate text-xs text-[var(--dash-muted)]">
            {company && company !== name
              ? name
              : [category, subCategory].filter(Boolean).join(' · ') || 'Client'}
          </p>
        </div>
      </div>

      <div className="mx-4 grid grid-cols-1 gap-2 rounded-xl bg-[var(--dash-soft)] p-3 text-left">
        <div className="flex items-center gap-2 text-xs text-[var(--dash-muted)]">
          <Mail className="h-3.5 w-3.5 shrink-0 opacity-70" />
          <span className="truncate text-[var(--dash-ink)]">
            {client.email || '—'}
          </span>
        </div>
        <div className="flex items-center gap-2 text-xs text-[var(--dash-muted)]">
          <Phone className="h-3.5 w-3.5 shrink-0 opacity-70" />
          <span className="truncate text-[var(--dash-ink)]">{phone || '—'}</span>
        </div>
        {(category || subCategory) && (
          <div className="flex items-center gap-2 text-xs text-[var(--dash-muted)]">
            <Building2 className="h-3.5 w-3.5 shrink-0 opacity-70" />
            <span className="truncate text-[var(--dash-ink)]">
              {[category, subCategory].filter(Boolean).join(' · ')}
            </span>
          </div>
        )}
      </div>

      <div className="mt-auto flex gap-2 p-4 pt-3">
        <button
          type="button"
          onClick={() => onView(client._id)}
          className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-[var(--dash-brand)] py-2.5 text-sm font-semibold text-white transition group-hover:brightness-105"
        >
          View
          <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
        </button>
        <button
          type="button"
          onClick={() => onEdit(client._id)}
          className="inline-flex h-[42px] w-[42px] items-center justify-center rounded-xl border border-[var(--dash-line)] text-[var(--dash-muted)] transition hover:border-[var(--dash-brand)] hover:bg-[var(--dash-brand-soft)] hover:text-[var(--dash-brand)]"
          title="Edit client"
          aria-label="Edit client"
        >
          <Pencil className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => onDelete(client._id)}
          className="inline-flex h-[42px] w-[42px] items-center justify-center rounded-xl border border-[var(--dash-line)] text-[var(--dash-muted)] transition hover:border-red-200 hover:bg-red-50 hover:text-red-500"
          title="Delete client"
          aria-label="Delete client"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </article>
  );
}
